import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useCartStore } from '@white-label-store/cart';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import { useSearchResultsStore } from '@white-label-store/search-results';
import { useManageBookingStore } from '@white-label-store/manage-booking'
import { EXTRAS_PRODUCT_CODES, ROUTE_NAMES, PRODUCT_TYPES, ProductType } from '@white-label-configuration/constants';
import { SearchProducts } from '@white-label-types/parking-booking';
import { getAppVariable } from '@white-label-helper/get-app-variable';

export interface Traveller {
  id: string;
  dateOfBirth: Date | string;
  countryOfResidence?: { name: string; id: string };
  isPrimary: boolean;
}

export type TravelInsuranceForm = {
  destination: string[];
  travelDates: {
    startDate: Date | string;
    endDate: Date | string;
  };
  travellers: Traveller[];
};


export const useTravelInsuranceStore = defineStore('travelInsurance', () => {
  const form = ref<TravelInsuranceForm>({
    destination: [],
    travelDates: {
      startDate: '',
      endDate: '',
    },
    travellers: [
      {
        id: '1',
        dateOfBirth: '',
        countryOfResidence: { name: '', id: '' },
        isPrimary: true
      }
    ],
  });

  const activeStep = ref(0);
  const quoteResults = ref<SearchProducts | null>(null);
  const cartId = ref<string | null>(null);
  const selectedAddOnIds = ref<string[]>([]);
  const searchToken = ref<string | null>(null);
  const quoteModalProductId = ref<number | null>(null);
  const travelInsuranceVisibleInSearchResults = ref(false);


  // check for travel insurance product in results
  const hasTravelInsuranceInResults = computed(() => {
    const staticProductCards = getAppVariable<{ product_type: ProductType }[]>('static_product_cards', []);
    const isConfiguredForTravelInsurance = staticProductCards.some(card => card.product_type === PRODUCT_TYPES.TRAVEL_INSURANCE);
    return isConfiguredForTravelInsurance && travelInsuranceVisibleInSearchResults.value;
  });

  // check for travel insurance in the availability call metadata and config
  const isTravelInsuranceAvailableInMetadataOrConfig = computed(() => {
    const productTypesAvailable = getAppVariable<ProductType[]>('product_types_available', []);
    const searchResultsStore = useSearchResultsStore();
    if (!searchResultsStore.inventoryMetadata || Object.keys(searchResultsStore.inventoryMetadata).length === 0) {
      return productTypesAvailable.includes(PRODUCT_TYPES.TRAVEL_INSURANCE)
    } else {
      const travelInsurance = searchResultsStore.inventoryMetadata?.['has_travel_insurance'];
      return travelInsurance === true;
    }
  });

  // check for travel insurance in cart
  const travelInsuranceCartItem = computed(() => {
    const cartStore = useCartStore();
    const cartCheckoutStore = useCartCheckoutStore();
    const cartItems = cartStore.readCartItems;
    const checkoutCartItems = cartCheckoutStore.readCartItems;

    const items = cartItems.length ? cartItems : checkoutCartItems;

    if (!items || items.length === 0) {
      return null;
    }

    return items.find(item =>
      item.product_code === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE ||
      item.product_code === 'travel-insurance' ||
      item.product_type === 'travel-insurance'
    ) || null;
  });

  const hasTravelInsuranceInCart = computed(() => {
    return travelInsuranceCartItem.value !== null;
  });

  const quoteStarted = computed(() => {
    const step1HasData = form.value.destination.length > 0;
    const step2HasData = form.value.travelDates?.startDate !== '' && form.value.travelDates?.endDate !== '';
    const step3HasData = form.value.travellers[0].countryOfResidence?.id !== '' && form.value.travellers[0].dateOfBirth !== '';
    return step1HasData || step2HasData || step3HasData;
  });

  const travelInsuranceOrderItem = computed(() => {
    const manageBookingStore = useManageBookingStore();
    const manageBookingItems = manageBookingStore.readManageBookingItems;

    if (manageBookingItems?.length) {
      return manageBookingItems.find(
        (item) => item.product_code === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE
      ) || null;
    }

    return null;
  });

  const hasTravelInsuranceInOrder = computed(() => {
    return travelInsuranceOrderItem.value !== null;
  });

  // check if TI product is in search results
  function setTravelInsuranceVisibleInSearchResults(value: boolean) {
    travelInsuranceVisibleInSearchResults.value = value;
  }

  // check if disclosure message should show up
  function shouldShowDisclosure(currentRouteName?: string): boolean {
    if (currentRouteName === ROUTE_NAMES.home || currentRouteName === ROUTE_NAMES.search) {
      return false;
    }
    const isOnTravelExtrasPage = currentRouteName === ROUTE_NAMES.travel_extras;

    // show disclosure when either on travel-extras page and travel insurance is in results
    // or travel insurance is in cart(payment page), or manage-booking pages
      return (
    (isOnTravelExtrasPage && hasTravelInsuranceInResults.value) ||
      hasTravelInsuranceInCart.value ||
      hasTravelInsuranceInOrder.value
    );
  }

  function setQuoteResults(results: SearchProducts[]) {
    if (!Array.isArray(results) || results.length === 0) {
      quoteResults.value = null;
      return;
    }

    const matchingProductIdResult = quoteModalProductId.value ? results.find(product => product.id === quoteModalProductId.value) : null;
    if (matchingProductIdResult) {
      quoteResults.value = matchingProductIdResult;
    } else {
      quoteResults.value = results[0];
    }
  }

  function setSearchToken(token: string) {
    searchToken.value = token;
  }

  function updateFormField<K extends keyof TravelInsuranceForm>(key: K, value: TravelInsuranceForm[K]) {
    form.value[key] = value;
  }

  // Form data is persisted so that data is not lost if the page is refreshed
  // However, if the cart id changes (i.e user starts a new order) then we need to clear the form
  function initForm() {
    const currentCartId = useCartStore().readToken;
    if (cartId.value && cartId.value !== currentCartId) {
      clearForm();
    }
    cartId.value = currentCartId;
  }

  function clearForm() {
    form.value = {
      destination: [],
      travelDates: {
        startDate: '',
        endDate: '',
      },
      travellers: [
        {
          id: '1',
          dateOfBirth: '',
          countryOfResidence: '',
          isPrimary: true
        }
      ],
    };
    activeStep.value = 0;
    quoteResults.value = null;
    searchToken.value = null;
    cartId.value = null;
  }

  return {
    activeStep,
    cartId,
    clearForm,
    form,
    hasTravelInsuranceInResults,
    travelInsuranceVisibleInSearchResults,
    setTravelInsuranceVisibleInSearchResults,
    isTravelInsuranceAvailableInMetadataOrConfig,
    hasTravelInsuranceInCart,
    initForm,
    selectedAddOnIds,
    setQuoteResults,
    setSearchToken,
    shouldShowDisclosure,
    travelInsuranceCartItem,
    quoteResults,
    searchToken,
    quoteModalProductId,
    quoteStarted,
    updateFormField,
    hasTravelInsuranceInOrder
  };
}, {
  persist: true,
});
